













































import { Component, Inject, Prop, Vue, Watch } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";

import EditableText from "@/modules/plushie/components/EditableText.vue";
import TrackingNumber from "@/lib/components/TrackingNumber.vue";

import dataStore from "@/store";
import PlushieStore from "@/modules/plushie/store";
import TrackingInformation from "@/modules/plushie/tracking-information.model";
import AuthenticatedUserProvider from "@/modules/account/authenticated-user-provider.service";
import { Resource } from "@/modules/account/resource";

import EditableWithSelect from "./EditableWithSelect.vue";

import Carrier from "../carrier.model";

@Component({
  components: {
    EditableText,
    EditableWithSelect,
    TrackingNumber,
  },
})
export default class PlushieShipmentInfo extends Vue {
  @Prop({ required: true })
  public readonly plushieId!: string;

  @Inject("AuthenticatedUserProvider")
  private fUserProvider!: AuthenticatedUserProvider;

  private fIsLoading = true;

  private fPlushieStore: PlushieStore;

  get canManageTrackingInformation(): boolean {
    const user = this.fUserProvider.getUser();

    if (!user) {
      return false;
    }

    return user.hasPermissionForResource(Resource.TRACKING_INFORMATION_MANAGE);
  }

  get isLoading(): boolean {
    return this.fIsLoading;
  }

  get createdAt(): string {
    return this.trackingInformation?.createdAt?.toLocaleDateString() || "";
  }

  get showComponent(): boolean {
    return !this.isLoading && !!this.trackingNumber;
  }

  get trackingInformation(): TrackingInformation | undefined {
    return this.fPlushieStore.getTrackingInformationById(this.plushieId);
  }

  get trackingNumber(): string {
    return this.trackingInformation?.trackingNumber || "";
  }

  get carrierId(): string {
    return this.trackingInformation?.carrier || "";
  }

  get selectedCarrier(): Carrier | undefined {
    return this.fPlushieStore.getCarrierById(this.carrierId);
  }

  get carrierName(): string {
    return this.selectedCarrier?.name || "";
  }

  get carrierOptions(): { value: string; name: string }[] {
    return this.fPlushieStore.carriersList.map((carrier) => ({
      value: carrier.id,
      name: carrier.name,
    }));
  }

  constructor() {
    super();

    this.fPlushieStore = getModule(PlushieStore, dataStore);
  }

  public async updateTrackingNumber(value?: string): Promise<void> {
    if (!value || !this.trackingInformation) {
      return;
    }

    const trackingInformation = new TrackingInformation(
      this.trackingInformation.id,
      value,
      this.trackingInformation.carrier
    );

    trackingInformation.isNew = false;

    await this.fPlushieStore.saveTrackingInformation(trackingInformation);
  }

  public async updateCarrier(value?: string): Promise<void> {
    if (!value || !this.trackingInformation) {
      return;
    }

    const trackingInformation = new TrackingInformation(
      this.trackingInformation.id,
      this.trackingInformation.trackingNumber,
      value
    );

    trackingInformation.isNew = false;

    await this.fPlushieStore.saveTrackingInformation(trackingInformation);
  }

  private async loadData(
    plushieId: string
  ): Promise<TrackingInformation | undefined> {
    const [trackingInformation] = await Promise.all([
      this.fPlushieStore.loadTrackingInformationById({
        id: plushieId,
      }),
      this.fPlushieStore.loadCarriers(),
    ]);

    return trackingInformation;
  }

  @Watch("plushieId", { immediate: true })
  private async _onPlushieIdChange(): Promise<void> {
    if (!this.plushieId) {
      return;
    }

    this.fIsLoading = true;

    try {
      await this.loadData(this.plushieId);
    } finally {
      this.fIsLoading = false;
    }
  }
}
